import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import { BodyText, Button, Collapsible, Icons } from "@/components/ui";
import { Separator } from "@/components/ui";
import { formatDateTime } from "@/lib/time";
import { hasMarkdownFormatting } from "@/lib/utils";
import { useState } from "react";
import { type Addendum, AddendumStatus } from "./types";

interface AVCallSummaryAddendumProps {
  summary: string;
  time: string;
  status?: AddendumStatus;
  addendumId: string;
  onAddAddendum: (addendum: Addendum) => void;
  addedAddendums: Addendum[];
  canWriteSubjective: boolean;
}

const AUTHOR_LABEL = "AV Call Summary";

export const AVCallSummaryAddendum = ({
  summary,
  time,
  status,
  addendumId,
  onAddAddendum,
  addedAddendums,
  canWriteSubjective,
}: AVCallSummaryAddendumProps) => {
  const [open, setOpen] = useState(true);

  // Derived from props — no local state needed. Parent owns addedAddendums,
  // so on click the parent's setState triggers a re-render and isAdded
  // recomputes naturally.
  const isAdded =
    status === AddendumStatus.ACCEPTED ||
    addedAddendums.some((a) => a.id === addendumId);

  const handleAddToSubjective = () => {
    const timestamp = formatDateTime(time);
    const prefixedText = `\n**[${AUTHOR_LABEL} ${timestamp}]**\n\n${summary}`;
    onAddAddendum({ id: addendumId, text: prefixedText });
    setOpen(false);
  };

  const handleHide = () => {
    setOpen(false);
  };

  const getTitle = () => {
    if (!summary) return AUTHOR_LABEL;

    const words = summary.split(" ");
    const shortTitle = words.slice(0, 3).join(" ");
    const needsEllipsis = words.length >= 4;

    return `${AUTHOR_LABEL} - ${shortTitle}${needsEllipsis ? "..." : ""}`;
  };

  return (
    <>
      <div className="p-3">
        <Collapsible
          title={getTitle()}
          open={open}
          controlled={true}
          headerSize="sm"
          headerTextColor="superclinical"
          border="none"
          status={formatDateTime(time)}
          statusBadge={
            isAdded ? (
              <Icons.Check className="h-3 w-3 text-tertiary" />
            ) : undefined
          }
          background={open ? "superclinical" : "default"}
          className={open ? "p-2" : ""}
          onOpenChange={(open) => {
            setOpen(open);
          }}
        >
          <div className="flex flex-col mb-2">
            {hasMarkdownFormatting(summary) ? (
              <MarkdownRenderer content={summary} />
            ) : (
              <BodyText size="sm" preserveLineBreaks>
                {summary}
              </BodyText>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              onClick={handleHide}
              variant="secondary"
              size="xs"
              className="border-ai text-superclinical"
              leftIcon={Icons.EyeOff}
            >
              Hide
            </Button>
            {isAdded ? (
              <Button
                onClick={handleAddToSubjective}
                size="xs"
                variant="secondary"
                className="border-ai text-superclinical"
                leftIcon={Icons.RotateCcw}
                disabled={!canWriteSubjective}
              >
                Re-add to Subjective
              </Button>
            ) : (
              <Button
                onClick={handleAddToSubjective}
                size="xs"
                className="border-ai bg-button-superclinical text-white"
                leftIcon={Icons.ArrowUp}
                disabled={!canWriteSubjective}
              >
                Add to Subjective
              </Button>
            )}
          </div>
        </Collapsible>
      </div>
      <Separator />
    </>
  );
};
